import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import TamilField from '@/components/common/TamilField'
import BulkTranslate from '@/components/common/BulkTranslate'
import { Alert, Empty, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import Confirm from '@/components/common/Confirm'
import { PERMS, can, useAuth } from '@/store/auth'

const BLANK = { code: '', label: '', description: '', sort_order: 100, label_ta: '' }

// Hidden, not removed. Flip either back to true to show it again; the
// bulk-translate drawer and the Tamil data are untouched.
const SHOW_BULK_TRANSLATE = false
const SHOW_TAMIL_COLUMN = false
// The count still guards deleting a unit (see inUse); only the column is hidden.
const SHOW_ORDER_LINES_COLUMN = false

/**
 * The units the catalog and orders are written in.
 *
 * A vocabulary, not a conversion table: products still store their unit as
 * text and price through their own conversion rate, so adding a unit here
 * changes no arithmetic. That is also why a unit in use cannot be deleted —
 * nothing would cascade, it would just stop being listed.
 */
export default function Units() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [form, setForm] = useState(null)
  const [confirming, setConfirming] = useState(null)
  // Rows ticked for bulk translation, by id.
  const [picked, setPicked] = useState({})
  const [bulk, setBulk] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const editable = can(user, PERMS.productsWrite)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['units'],
    queryFn: () => unitsApi.list(),
  })

  const done = (message) => {
    queryClient.invalidateQueries({ queryKey: ['units'] })
    setForm(null)
    setError('')
    setNotice(message)
  }

  const save = useMutation({
    mutationFn: ({ id, ...payload }) =>
      id ? unitsApi.update(id, payload) : unitsApi.create(payload),
    onSuccess: (unit) => done(`Saved ${unit.code}.`),
    onError: (e) => setError(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: unitsApi.remove,
    onSuccess: () => done('Unit deleted.'),
    onError: (e) => setError(errorMessage(e)),
  })

  const toggle = (unit) =>
    save.mutate({ id: unit.id, is_active: !unit.is_active })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load units" onRetry={refetch} />

  const inUse = (unit) => unit.product_count + unit.order_line_count

  // 'Pending' means no approved Tamil yet — the rows a bulk run would cover.
  const pending = data.filter((u) => !u.label_ta)
  const chosen = data.filter((u) => picked[u.id])
  const selectAllPending = () =>
    setPicked(Object.fromEntries(pending.map((u) => [u.id, true])))

  return (
    <div className="units-page">
      <PageHeader
        title="Units"
        subtitle="How quantities are expressed on products and on order lines"
        actions={
          <>
            <Link to="/products/unit-weights" className="btn btn-sm">
              Unit weights
            </Link>
            <Link to="/products" className="btn btn-sm">
              Back to products
            </Link>
            {SHOW_BULK_TRANSLATE && editable && pending.length > 0 && (
              <button type="button" className="btn btn-sm" onClick={selectAllPending}>
                Select all pending ({pending.length})
              </button>
            )}
            {SHOW_BULK_TRANSLATE && editable && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={chosen.length === 0}
                title={
                  chosen.length === 0
                    ? 'Tick the units to translate, or use Select all pending'
                    : undefined
                }
                onClick={() => {
                  setNotice('')
                  setBulk(true)
                }}
              >
                Translate selected ({chosen.length})
              </button>
            )}
            {editable && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setNotice('')
                  setForm(BLANK)
                }}
              >
                Add unit
              </button>
            )}
          </>
        }
      />

      {/* One slot, always the height of an alert: switching a unit on and off
          repeatedly used to move the table down and back as the message came
          and went. An error takes the slot when there is one; otherwise the
          success message does, and an empty slot holds the space. */}
      {!form && error ? (
        <Alert tone="danger" message={error} onClose={() => setError('')} />
      ) : (
        <Alert tone="success" message={notice} onClose={() => setNotice('')} reserve />
      )}

      <Drawer
        open={Boolean(form)}
        error={error}
        onErrorDismiss={() => setError('')}
        title={form?.id ? 'Edit unit' : 'Add unit'}
        subtitle={form?.id ? form.label : 'Saved in upper case, as products already write it.'}
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="unit-form"
              className="btn btn-primary"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {form && (
          <form id="unit-form" onSubmit={(e) => {
            e.preventDefault()
            save.mutate(form)
          }}>
          <div className="form-grid">
            {!form.id && (
              <label className="field">
                <span>Code *</span>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="CRATES"
                  required
                  autoFocus
                />
              </label>
            )}
            <label className="field">
              <span>Shown as</span>
              <input
                value={form.label ?? ''}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Crates"
              />
            </label>
            <TamilField
              value={form.label_ta}
              onChange={(label_ta) => setForm((f) => ({ ...f, label_ta }))}
              source={form.label || form.code}
              entityType="unit"
              label="Shown as (Tamil)"
              hint="What a customer writing in Tamil sees. Optional — English is what the catalog runs on."
            />
            <label className="field">
              <span>Sort order</span>
              <input
                type="number"
                value={form.sort_order ?? 100}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
              />
            </label>
          </div>
          <p className="hint">
            {form.id
              ? 'The code is fixed: products and order lines store it as text, so changing it would orphan them.'
              : 'Saved in upper case, the way products and order lines already write it.'}
          </p>
          </form>
        )}
      </Drawer>


      {!data.length && <Empty icon="⚖️" title="No units yet" />}

      {data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {SHOW_BULK_TRANSLATE && editable && (
                <th className="tick">
                  <input
                    type="checkbox"
                    aria-label="Select every unit without Tamil"
                    checked={pending.length > 0 && chosen.length === pending.length}
                    onChange={(e) =>
                      e.target.checked ? selectAllPending() : setPicked({})
                    }
                  />
                </th>
              )}
              <th>Code</th>
              <th>Shown as</th>
              {SHOW_TAMIL_COLUMN && <th>Tamil</th>}
              <th className="right">Products</th>
              {SHOW_ORDER_LINES_COLUMN && <th className="right">Order lines</th>}
              <th className="after-number">Status</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {data.map((unit) => (
              <tr key={unit.id} className={unit.is_active ? '' : 'inactive'}>
                {SHOW_BULK_TRANSLATE && editable && (
                  <td className="tick">
                    <input
                      type="checkbox"
                      aria-label={`Select ${unit.code}`}
                      checked={Boolean(picked[unit.id])}
                      onChange={(e) =>
                        setPicked((p) => ({ ...p, [unit.id]: e.target.checked }))
                      }
                    />
                  </td>
                )}
                <td className="strong mono">{unit.code}</td>
                <td>
                  {unit.label}
                  {unit.label_ta && <span className="ta-line" lang="ta">{unit.label_ta}</span>}
                </td>
                {SHOW_TAMIL_COLUMN && (
                  <td>
                    {unit.label_ta ? (
                      <span className="ta-status done">✓ Translated</span>
                    ) : (
                      <span className="ta-status missing">Not translated</span>
                    )}
                  </td>
                )}
                <td className="right">{unit.product_count || <span className="muted">0</span>}</td>
                {SHOW_ORDER_LINES_COLUMN && (
                  <td className="right">
                    {unit.order_line_count || <span className="muted">0</span>}
                  </td>
                )}
                <td className="after-number">{unit.is_active ? 'In use' : 'Switched off'}</td>
                {editable && (
                  <td className="right nowrap">
                    <div className="row-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setNotice('')
                        setForm({
                          id: unit.id,
                          label: unit.label,
                          sort_order: unit.sort_order,
                          label_ta: unit.label_ta ?? '',
                        })
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      // save.isPending is page-wide: reading it here dimmed
                      // every row's button while one row saved. The in-flight
                      // arguments say which row it actually is.
                      disabled={save.isPending && save.variables?.id === unit.id}
                      onClick={() => {
                        setNotice('')
                        toggle(unit)
                      }}
                    >
                      {unit.is_active ? 'Switch off' : 'Switch on'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={inUse(unit) > 0 || remove.variables === unit.id}
                      title={
                        inUse(unit) > 0
                          ? `Used by ${unit.product_count} product(s) and ${unit.order_line_count} order line(s) — switch it off instead`
                          : undefined
                      }
                      onClick={() => {
                        setNotice('')
                        setConfirming(unit)
                      }}
                    >
                      Delete
                    </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="hint">
        This list names units. What one of them <em>weighs</em> is set per
        category on <Link to="/products/unit-weights">unit weights</Link> —
        without a weight there, an order in rolls or bundles cannot be priced
        and goes to the review queue.
      </p>
      <BulkTranslate
        open={bulk}
        entityType="unit"
        label="units"
        selectedIds={chosen.map((u) => u.id)}
        onClose={() => setBulk(false)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['units'] })
          setPicked({})
        }}
      />
      <Confirm
        open={Boolean(confirming)}
        title={`Delete the ${confirming?.code ?? ''} unit?`}
        message="Nothing that already uses this unit changes — it only stops being offered."
        busy={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          remove.mutate(confirming.id)
          setConfirming(null)
        }}
      />
    </div>
  )
}
